import { ColorPicker } from "@smartsuite/react-ui/lib/components/dropdowns/ColorPicker/ColorPicker";
import { InputGroup } from "@smartsuite/react-ui/lib/components/forms/InputGroup/InputGroup";
import { Text } from "@smartsuite/react-ui/lib/components/typography/Text/Text";
import { Icon } from "@smartsuite/react-ui/lib/components/utils/Icon/Icon";
import cx from "classnames";
import type React from "react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CustomPickerColor } from "../CustomPickerColor/CustomPickerColor";
import { CustomPickerColorSelect } from "../CustomPickerColorSelect/CustomPickerColorSelect";
import { showElementWithoutImplementation } from "../utils";
import { colors } from "./ColorPickerField.contants";

export interface ColorPickerFieldProps {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
}

export const ColorPickerField: React.FC<ColorPickerFieldProps> = ({
  label,
  value,
  onChange,
}) => {
  const customColorSelectRef = useRef<HTMLDivElement>(null);
  const selectedValue = useMemo(
    () => colors.find((item) => item.color === value),
    [value],
  );
  const [customColors, setCustomColors] = useState<string[]>([]);
  const [isColorMixerActive, setIsColorMixerActive] = useState(false);
  const [colorPickerDropdownHeight, setColorPickerDropdownHeight] = useState<
    number | "auto"
  >();
  const [isOpen, setIsOpen] = useState(false);

  const handleCancelCustomColor = useCallback(() => {
    setIsColorMixerActive(false);
  }, [setIsColorMixerActive]);

  const handleAddCustomColor = useCallback(
    (newColor: string) => {
      setCustomColors((prevColors) => {
        return [...prevColors, newColor];
      });
      setIsColorMixerActive(false);
      onChange(newColor);
    },
    [onChange],
  );

  const handleActiveCustomMixerColor = useCallback(() => {
    setIsColorMixerActive(true);
  }, []);

  const handleOpenColorPicker = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, [setIsOpen]);

  const classes = cx("color-picker-content", {
    "color-picker-content--mixer-active": isColorMixerActive,
  });

  useLayoutEffect(() => {
    if (isColorMixerActive) {
      const height = customColorSelectRef.current?.offsetHeight;
      setColorPickerDropdownHeight(height);

      return;
    }

    setColorPickerDropdownHeight("auto");
  }, [isColorMixerActive]);

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <Text
          weight="medium"
          color={isOpen ? "link" : "primary"}
          wrapMode="ellipsis"
        >
          {label}
        </Text>
      </div>

      <InputGroup
        state={isOpen ? "active" : "default"}
        className="sidebar-selection-config__color-picker"
        onClick={handleOpenColorPicker}
      >
        <ColorPicker
          at="left-6 bottom+8"
          isOpen={isOpen}
          dropdownClassname="color-picker-dropdown"
          colorPaletteStyles={{
            height: colorPickerDropdownHeight,
          }}
          dropdownWidth={318}
          className="sidebar-selection-config__palette"
          zIndex={5}
          colors={colors}
          selectedColor={value ?? colors[0].name}
          onChange={onChange}
          onOpen={setIsOpen}
        >
          {showElementWithoutImplementation && (
            <div className={classes}>
              {!isColorMixerActive && (
                <CustomPickerColor
                  className="color-picker-content__custom-colors"
                  colors={customColors}
                  selectedColor={value}
                  onAddCustomColor={handleActiveCustomMixerColor}
                  onChange={onChange}
                />
              )}
              <CustomPickerColorSelect
                ref={customColorSelectRef}
                className="color-picker-content__mixer"
                onChange={handleAddCustomColor}
                onCancel={handleCancelCustomColor}
              />
            </div>
          )}
        </ColorPicker>
        <span>{selectedValue?.name ?? colors[0].name}</span>
        <Icon
          className="color-picker-content__arrow"
          name="arrow-down-small"
          color="currentColor"
          size="M"
        />
      </InputGroup>
    </div>
  );
};
